import { ColumnDef } from "@tanstack/react-table";
import { ArrowUpDown, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AssetDisplay } from "@/components/ui/asset-display";
import type { Asset } from "@/schemas";
import { Link } from "@tanstack/react-router";

export type AssetTablePerformance = Asset & {
  current_value: number;
  performance_absolute: number;
  performance_percentage: number;
  performance_absolute_native: number;
  performance_percentage_native: number;
  asset_currency: string;
  avg_cost_basis?: number | null;
  avg_cost_basis_native?: number | null;
  shares_owned?: number | null;
  // P&L Breakdown
  realized_pnl?: number;
  realized_pnl_native?: number;
  unrealized_pnl?: number;
  unrealized_pnl_native?: number;
  dividends_received?: number;
  dividends_received_native?: number;
  // Position status
  is_closed?: boolean;
  has_history?: boolean;
  /** 1d portfolio performance: change in position value in base currency */
  daily_pnl_base?: number;
  /** 1d portfolio performance: % change on start-of-day position value */
  daily_return_pct?: number;
  /** Quote daily % change (from market data), if available */
  asset_quote_daily_change_pct?: number;
  /** Share of portfolio total value (0–100) */
  weight_pct?: number;
};

const formatPercentage = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);

interface ColumnOptions {
  currencyCode: string;
  onViewTrades?: (asset: AssetTablePerformance) => void;
  isMobile?: boolean;
  optionalColumnsPicker?: boolean;
}

export const getColumns = (
  options: ColumnOptions,
): ColumnDef<AssetTablePerformance>[] => {
  const { currencyCode, onViewTrades, isMobile, optionalColumnsPicker } =
    options;
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  return [
    {
      accessorKey: "asset_name",
      enableHiding: false,
      meta: { sticky: true, className: "w-[200px] max-w-[200px]" },
      header: "Asset",
      cell: ({ row }) => {
        const asset = row.original;
        const isClosed = asset.is_closed === true;
        return (
          <div className="flex items-center gap-2">
            <Link
              to="/screener/$assetId"
              params={{ assetId: asset.asset_id }}
              className="hover:underline cursor-pointer min-w-0"
            >
              <AssetDisplay asset={asset} size="sm" />
            </Link>
            {isClosed && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
                CLOSED
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      enableHiding: false,
      header: () => <div className="text-center">Trades</div>,
      cell: ({ row }) => {
        const asset = row.original;
        return (
          <div className="text-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onViewTrades?.(asset);
                    }}
                  >
                    <LineChart className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View trades on chart</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },
    ...(optionalColumnsPicker
      ? ([
          {
            id: "last_price",
            accessorFn: (row) => row.latest_price ?? null,
            enableHiding: true,
            meta: { columnLabel: "Last price" },
            header: ({ column }) => (
              <div className="text-right">
                <Button
                  variant="ghost"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                  className="h-7 px-1 text-sm justify-end"
                >
                  Last price
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
              </div>
            ),
            cell: ({ row }) => {
              const asset = row.original;
              const price = asset.latest_price;
              if (price === undefined || price === null) {
                return (
                  <div className="text-right font-mono text-sm text-muted-foreground">
                    —
                  </div>
                );
              }
              const code = asset.asset_currency || "USD";
              const fmt = new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: code,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return (
                <div className="text-right font-mono text-sm">{fmt.format(price)}</div>
              );
            },
          },
          {
            id: "asset_quote_daily_change_pct",
            accessorFn: (row) => row.asset_quote_daily_change_pct ?? null,
            enableHiding: true,
            meta: { columnLabel: "Quote daily %" },
            header: ({ column }) => (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-right">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          column.toggleSorting(column.getIsSorted() === "asc")
                        }
                        className="h-7 px-1 text-sm justify-end max-w-full"
                      >
                        Day (quote)
                        <ArrowUpDown className="ml-1 h-3 w-3 shrink-0" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>
                      Underlying price change today from market data (not your
                      position return).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ),
            cell: ({ row }) => {
              const v = row.original.asset_quote_daily_change_pct;
              if (v === undefined || v === null) {
                return (
                  <div className="text-right font-mono text-sm text-muted-foreground">
                    —
                  </div>
                );
              }
              const isPositive = v >= 0;
              return (
                <div
                  className="text-right font-mono text-sm"
                  style={{
                    color: isPositive ? "#10b981" : "#ef4444",
                  }}
                >
                  {isPositive ? "+" : ""}
                  {formatPercentage(v)}
                </div>
              );
            },
          },
          {
            id: "daily_pnl_base",
            accessorFn: (row) => row.daily_pnl_base ?? null,
            enableHiding: true,
            meta: { columnLabel: "Daily P&L" },
            header: ({ column }) => (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-right">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          column.toggleSorting(column.getIsSorted() === "asc")
                        }
                        className="h-7 px-1 text-sm justify-end max-w-full"
                      >
                        Daily P&L
                        <ArrowUpDown className="ml-1 h-3 w-3 shrink-0" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>
                      Change in this line&apos;s value over the last trading day
                      (1d), in your portfolio currency.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ),
            cell: ({ row }) => {
              const v = row.original.daily_pnl_base;
              if (v === undefined || v === null) {
                return (
                  <div className="text-right font-mono text-sm text-muted-foreground">
                    —
                  </div>
                );
              }
              const isPositive = v >= 0;
              return (
                <div
                  className="text-right font-mono text-sm font-medium"
                  style={{
                    color: isPositive ? "#10b981" : "#ef4444",
                  }}
                >
                  {isPositive ? "+" : ""}
                  {formatCurrency(v)}
                </div>
              );
            },
          },
          {
            id: "daily_return_pct",
            accessorFn: (row) => row.daily_return_pct ?? null,
            enableHiding: true,
            meta: { columnLabel: "Daily %" },
            header: ({ column }) => (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-right">
                      <Button
                        variant="ghost"
                        onClick={() =>
                          column.toggleSorting(column.getIsSorted() === "asc")
                        }
                        className="h-7 px-1 text-sm justify-end max-w-full"
                      >
                        Daily %
                        <ArrowUpDown className="ml-1 h-3 w-3 shrink-0" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>
                      Your position&apos;s return over the last trading day (1d),
                      based on portfolio value in your base currency.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ),
            cell: ({ row }) => {
              const v = row.original.daily_return_pct;
              if (v === undefined || v === null) {
                return (
                  <div className="text-right font-mono text-sm text-muted-foreground">
                    —
                  </div>
                );
              }
              const isPositive = v >= 0;
              return (
                <div
                  className="text-right font-mono text-sm"
                  style={{
                    color: isPositive ? "#10b981" : "#ef4444",
                  }}
                >
                  {isPositive ? "+" : ""}
                  {formatPercentage(v)}
                </div>
              );
            },
          },
          {
            id: "weight_pct",
            accessorFn: (row) => row.weight_pct ?? null,
            enableHiding: true,
            meta: { columnLabel: "Weight" },
            header: ({ column }) => (
              <div className="text-right">
                <Button
                  variant="ghost"
                  onClick={() =>
                    column.toggleSorting(column.getIsSorted() === "asc")
                  }
                  className="h-7 px-1 text-sm justify-end"
                >
                  Weight
                  <ArrowUpDown className="ml-1 h-3 w-3" />
                </Button>
              </div>
            ),
            cell: ({ row }) => {
              const v = row.original.weight_pct;
              if (v === undefined || v === null) {
                return (
                  <div className="text-right font-mono text-sm text-muted-foreground">
                    —
                  </div>
                );
              }
              return (
                <div className="text-right font-mono text-sm">
                  {new Intl.NumberFormat("en-US", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  }).format(v)}
                  %
                </div>
              );
            },
          },
        ] as ColumnDef<AssetTablePerformance>[])
      : []),
    {
      accessorKey: "current_value",
      enableHiding: false,
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Value
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("current_value"));
        return (
          <div className="text-right font-mono text-sm">
            {formatCurrency(value)}
          </div>
        );
      },
    },
    {
      accessorKey: "performance_absolute",
      enableHiding: false,
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Net P&L
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("performance_absolute"));
        const asset = row.original;
        const realizedPnl = asset.realized_pnl ?? 0;
        const unrealizedPnl = asset.unrealized_pnl ?? 0;
        const isPositive = value >= 0;

        const hasRealized = Math.abs(realizedPnl) > 0.01;

        const mainPnlContent = (
          <div
            className="font-mono text-sm font-bold"
            style={{
              color: isPositive ? "#10b981" : "#ef4444",
            }}
          >
            {isPositive ? "+" : ""}
            {formatCurrency(value)}
          </div>
        );

        if (!hasRealized) {
          return (
            <div className="flex flex-col items-end">{mainPnlContent}</div>
          );
        }

        return (
          <div className="flex flex-col items-end">
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="cursor-help underline decoration-dashed decoration-muted-foreground/30 underline-offset-4"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {mainPnlContent}
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  align="end"
                  className="flex flex-col gap-1.5 p-3"
                >
                  <div className="flex justify-between items-center gap-4 text-xs">
                    <span className="text-muted-foreground">Unrealized:</span>
                    <span
                      className="font-mono font-medium"
                      style={{
                        color: unrealizedPnl >= 0 ? "#10b981" : "#ef4444",
                      }}
                    >
                      {unrealizedPnl >= 0 ? "+" : ""}
                      {formatCurrency(unrealizedPnl)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center gap-4 text-xs">
                    <span className="text-muted-foreground">Realized:</span>
                    <span
                      className="font-mono font-medium"
                      style={{
                        color: realizedPnl >= 0 ? "#10b981" : "#ef4444",
                      }}
                    >
                      {realizedPnl >= 0 ? "+" : ""}
                      {formatCurrency(realizedPnl)}
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      },
    },

    {
      accessorKey: "performance_percentage",
      enableHiding: false,
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Net Return
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("performance_percentage"));
        const isPositive = value >= 0;

        return (
          <div
            className="text-right font-mono text-sm"
            style={{
              color: isPositive ? "#10b981" : "#ef4444",
            }}
          >
            {isPositive ? "+" : ""}
            {formatPercentage(value)}
          </div>
        );
      },
    },
    {
      accessorKey: "performance_absolute_native",
      enableHiding: optionalColumnsPicker,
      meta: {
        className: optionalColumnsPicker ? "" : "hidden md:table-cell",
        columnLabel: "P&L (asset currency)",
      },
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Asset P&L
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("performance_absolute_native"));
        const asset = row.original;
        const isPositive = value >= 0;

        const nativeCurrency = asset.asset_currency || "USD";

        const nativeFormatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: nativeCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return (
          <div
            className="text-right font-mono text-sm italic"
            style={{
              color: isPositive ? "#10b981" : "#ef4444",
            }}
          >
            {isPositive ? "+" : ""}
            {nativeFormatter.format(value)}
          </div>
        );
      },
    },
    {
      accessorKey: "performance_percentage_native",
      enableHiding: optionalColumnsPicker,
      meta: {
        className: optionalColumnsPicker ? "" : "hidden md:table-cell",
        columnLabel: "Return (asset currency)",
      },
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Asset Return
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const value = parseFloat(row.getValue("performance_percentage_native"));
        const isPositive = value >= 0;
        return (
          <div
            className="text-right font-mono text-sm italic"
            style={{
              color: isPositive ? "#10b981" : "#ef4444",
            }}
          >
            {isPositive ? "+" : ""}
            {formatPercentage(value)}
          </div>
        );
      },
    },
    {
      accessorKey: "shares_owned",
      enableHiding: optionalColumnsPicker,
      meta: optionalColumnsPicker
        ? { columnLabel: "Quantity" }
        : undefined,
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Shares
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const value = row.original.shares_owned;
        if (value === undefined || value === null || row.original.is_closed) {
          return (
            <div className="text-right font-mono text-sm text-muted-foreground">
              -
            </div>
          );
        }

        const formatted = new Intl.NumberFormat("en-US", {
          maximumFractionDigits: isMobile ? 1 : 4,
        }).format(value);

        return <div className="text-right font-mono text-sm">{formatted}</div>;
      },
    },
    {
      id: "avg_cost_basis",
      accessorFn: (asset) => asset.avg_cost_basis_native ?? asset.avg_cost_basis,
      enableHiding: optionalColumnsPicker,
      meta: {
        className: optionalColumnsPicker ? "" : "hidden md:table-cell",
        columnLabel: "Average cost",
      },
      header: ({ column }) => {
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
              className="h-7 px-1 text-sm justify-end"
            >
              Avg Cost
              <ArrowUpDown className="ml-1 h-3 w-3" />
            </Button>
          </div>
        );
      },
      cell: ({ row }) => {
        const asset = row.original;
        const value = asset.avg_cost_basis_native ?? asset.avg_cost_basis;

        if (value === null || value === undefined) {
          return (
            <div className="text-right font-mono text-sm text-muted-foreground">
              -
            </div>
          );
        }

        const nativeCurrency = asset.asset_currency || "USD";

        const nativeFormatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: nativeCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return (
          <div className="text-right font-mono text-sm">
            {nativeFormatter.format(value)}
          </div>
        );
      },
    },
  ];
};
